import type { ExceptionDetailRow, ExceptionField, ExceptionLineItem } from '@/api/cockpit';

/**
 * ADO #700: header fields the operator must justify when editing. Mirrors
 * the key-field gate in backend/src/routes/v1/cockpit/operator-resolution.ts.
 */
export const KEY_HEADER_FIELD_KEYS: readonly string[] = [
  'po_number',
  'customer_number',
  'invoice_customer_number',
  'legal_entity',
  'site',
  'payment_terms',
  'delivery_terms',
  'delivery_mode',
  'requested_delivery_date',
  'requested_ship_date',
];

/**
 * Stable key used to associate an operator-supplied reason with a specific
 * field/line. Mirrors `reasonKeyForChange` in operator-resolution.ts so the
 * frontend and backend agree on the map keys without any extra translation
 * layer.
 */
export function fieldReasonKey(args: { fieldName: string; lineNumber: number | null }): string {
  if (args.lineNumber === null) return args.fieldName;
  return `${args.fieldName}:${args.lineNumber}`;
}

export interface ModifiedKeyField {
  /** Map key for fieldReasons / fieldReasonDrafts. */
  readonly reasonKey: string;
  /** Human-readable label used in the Save tooltip. */
  readonly label: string;
}

/**
 * Computes the set of modified key fields the operator must justify before
 * the cockpit save is allowed. Compares the current form state against the
 * initial values from the loaded exception row.
 */
export function computeModifiedKeyFields(args: {
  readonly data: ExceptionDetailRow | undefined;
  readonly mergedFields: Record<string, string>;
  readonly selectedProducts: Record<number, string>;
  readonly selectedUnits: Record<number, string>;
  readonly selectedQuantities: Record<number, string>;
  readonly excludedLines?: Record<number, { category: string; reason: string }>;
}): readonly ModifiedKeyField[] {
  if (!args.data) return [];
  const modified: ModifiedKeyField[] = [];

  for (const field of args.data.fields) {
    if (!KEY_HEADER_FIELD_KEYS.includes(field.key)) continue;
    const previous = previousHeaderValue(field);
    const current = args.mergedFields[field.key] ?? previous;
    if (current === previous) continue;
    modified.push({
      reasonKey: fieldReasonKey({ fieldName: field.key, lineNumber: null }),
      label: field.label,
    });
  }

  for (const line of args.data.lineItems) {
    const exclusion = args.excludedLines?.[line.lineNumber];
    if (exclusion && exclusion.reason.trim().length >= 8) {
      modified.push({
        reasonKey: fieldReasonKey({ fieldName: 'line_exclusion', lineNumber: line.lineNumber }),
        label: `Line ${line.lineNumber} Exclusion`,
      });
      continue;
    }
    const product = args.selectedProducts[line.lineNumber] ?? line.matchedProductCode ?? '';
    if (product && product !== (line.matchedProductCode ?? '')) {
      modified.push({
        reasonKey: fieldReasonKey({ fieldName: 'product', lineNumber: line.lineNumber }),
        label: `Line ${line.lineNumber} Product`,
      });
    }
    const unit = args.selectedUnits[line.lineNumber] ?? line.matchedUnitValue ?? '';
    if (unit && unit !== (line.matchedUnitValue ?? '')) {
      modified.push({
        reasonKey: fieldReasonKey({ fieldName: 'unit_of_measure', lineNumber: line.lineNumber }),
        label: `Line ${line.lineNumber} Unit`,
      });
    }
    const quantityRaw = args.selectedQuantities[line.lineNumber];
    if (quantityRaw !== undefined) {
      const quantity = quantityRaw.trim();
      const previousQuantity = previousLineQuantity(line);
      if (quantity.length > 0 && quantity !== previousQuantity) {
        modified.push({
          reasonKey: fieldReasonKey({ fieldName: 'quantity', lineNumber: line.lineNumber }),
          label: `Line ${line.lineNumber} Quantity`,
        });
      }
    }
  }

  return modified;
}

function previousHeaderValue(field: ExceptionField): string {
  if (field.key === 'requested_delivery_date' || field.key === 'requested_ship_date') return field.value ?? '';
  return field.matchedValue ?? field.value ?? '';
}

function previousLineQuantity(line: ExceptionLineItem): string {
  return line.quantity === null || line.quantity === undefined ? '' : String(line.quantity);
}
